
import { computed, defineComponent, ref, PropType, getCurrentInstance } from 'vue'
import get from 'lodash/get'
import clamp from 'lodash/clamp'
import { ActivationToEdit, AudienceSettingsSchemaItem } from '../../../../../types/GCB2'
import { AUDIENCE_BOOLEAN_SELECT_DEFAULT_LIST } from '../../../../../vars/GCB2'
import { declOfNum } from '../../../../../utils'
import AudienceNumberInput from './AudienceNumberInput.vue'
import AudienceDateInput from './AudienceDateInput.vue'
import AudienceTextInput from './AudienceTextInput.vue'
import AudienceSettingsBaseInput from '@/components/inputs/AudienceSettingsBaseInput.vue'
import CustomActivatorSelect from '../../../../inputs/CustomActivatorSelect.vue'
import CustomActivatorTreeSelect from '../../../../inputs/CustomActivatorTreeSelect.vue'
import SmallHint from '@/components/SmallHint.vue'
import { getMassEditClipboardKey, readMassEditClipboard } from './audienceMassEditClipboard'

export default defineComponent({
    name: 'AudienceSchemaInlineFields',
    components: {
        AudienceNumberInput,
        AudienceDateInput,
        AudienceTextInput,
        AudienceSettingsBaseInput,
        CustomActivatorSelect,
        CustomActivatorTreeSelect,
        SmallHint,
    },
    props: {
        items: { type: Array as PropType<AudienceSettingsSchemaItem[]>, default: () => [] },
        value: { type: Object, required: true },
        audienceId: { type: [Number, String], required: true },
        lists: { type: Object, required: true },
        massEdit: { type: Boolean, default: false },
        currentActivation: { type: Object as PropType<ActivationToEdit>, required: false },
    },
    setup(props) {
        const root = getCurrentInstance()!.proxy
        const getBooleanSelectList = (schemaItem: AudienceSettingsSchemaItem) => [
            { id: true, name: schemaItem.trueText ?? AUDIENCE_BOOLEAN_SELECT_DEFAULT_LIST.trueText },
            { id: false, name: schemaItem.falseText ?? AUDIENCE_BOOLEAN_SELECT_DEFAULT_LIST.falseText },
        ]
        const getComponentType = (schemaItem: AudienceSettingsSchemaItem) => {
            if (!schemaItem) return undefined
            if (schemaItem.type === 'select' && schemaItem.list === 'service_list') return 'treeSelect'
            if (schemaItem.type === 'select' || schemaItem.type === 'boolSelect') return 'select'
            return schemaItem.type
        }
        const getInputWidth = (item: AudienceSettingsSchemaItem) => clamp(item.width ?? 0, 70, Number.MAX_SAFE_INTEGER)
        const getList = (item: AudienceSettingsSchemaItem) => {
            if (item.type === 'boolSelect') return getBooleanSelectList(item)
            if (item.list) return props.lists[item.list as string]
            if (item.customList) return item.customList
        }
        const getInputValue = (item: AudienceSettingsSchemaItem, selected: any) =>
            item.multiple ? `${item.label} (${selected.length})` : selected?.name ?? ''
        const getShowTooltip = (item: AudienceSettingsSchemaItem) => get(item, 'showTooltip')
        const isToggleBlockVisible = (item: AudienceSettingsSchemaItem, audienceValues: Record<string, any>) => {
            const current = audienceValues[item.toggle as string]
            const expected = item.toggleValue
            if (Array.isArray(expected)) {
                return expected.includes(current)
            }
            return current === expected
        }
        const flattenVisibleItems = (
            items: AudienceSettingsSchemaItem[] | undefined,
            audienceValues: Record<string, any>,
        ): AudienceSettingsSchemaItem[] => {
            if (!Array.isArray(items)) return []
            const result: AudienceSettingsSchemaItem[] = []
            items.forEach((inner) => {
                if (!inner) return
                if (Array.isArray(inner)) {
                    result.push(...flattenVisibleItems(inner, audienceValues))
                    return
                }
                if (inner.type === 'toggleBlock') {
                    if (isToggleBlockVisible(inner, audienceValues)) {
                        result.push(...flattenVisibleItems(inner.content, audienceValues))
                    }
                    return
                }
                result.push(inner)
            })
            return result
        }
        const visibleItems = computed(() => {
            const audienceValues = props.value[props.audienceId] || {}
            return flattenVisibleItems(props.items, audienceValues)
        })
        const dropdownHintsState = ref<Record<string | number, boolean>>({})
        const isDropdownHintOpen = (id?: string | number) => (id != null ? !!dropdownHintsState.value[id] : false)
        const toggleDropdownHint = (id?: string | number) => {
            if (id == null) return
            dropdownHintsState.value = { ...dropdownHintsState.value, [id]: !dropdownHintsState.value[id] }
        }
        const getCollapseKey = (item: AudienceSettingsSchemaItem) =>
            item.collapseId != null ? item.collapseId : item.collapseText
        const collapsibleState = ref<Record<string | number, boolean>>({})
        const isCollapsibleOpen = (id?: string | number) => (id != null ? !!collapsibleState.value[id] : false)
        const toggleCollapsible = (id?: string | number) => {
            if (id == null) return
            collapsibleState.value = { ...collapsibleState.value, [id]: !collapsibleState.value[id] }
        }
        const copy = (valueField: string, list: any[], selectType: 'default' | 'tree', listKey?: string) => {
            const clearName = getMassEditClipboardKey(valueField, listKey)
            const value = props.value[props.audienceId][valueField] || []
            let updatedValue = []
            if (selectType === 'tree') {
                const flatList: any[] = []
                list.forEach(el => flatList.push(...el.children))
                updatedValue = flatList.filter(el => value.includes(el.id))
            } else {
                updatedValue = list.filter((el: any) => value.includes(el.id))
            }
            localStorage.setItem('massEdit-' + clearName, JSON.stringify({
                name: props.currentActivation?.name,
                projectId: props.currentActivation?.projectId,
                value: updatedValue,
            }))
            root.$store.dispatch('callNotify', `Скопировано в буфер обмена`)
        }
        const paste = (valueField: string, list: any[], selectType: 'default' | 'tree', listKey?: string) => {
            const string = readMassEditClipboard(valueField, listKey)
            if (!string) {
                root.$store.dispatch('callNotify', `Нет скопированных данных`)
                return
            }
            const json = JSON.parse(string)
            const clearValueNames = json.value.filter((el: any) => el.name).map((el: any) => el.name.toLowerCase())
            if (json.projectId === props.currentActivation?.projectId) {
                props.value[props.audienceId][valueField] = json.value.map((el: any) => el.id)
                return
            }
            if (selectType === 'tree') {
                const flatList: any[] = []
                list.forEach((el: any) => flatList.push(...el.children))
                const clearList = flatList.map((el: any) => ({ ...el, name: el.name.toLowerCase() }))
                props.value[props.audienceId][valueField] = clearList.filter((el: any) => clearValueNames.includes(el.name)).map((el: any) => el.id)
            } else {
                const clearList = list.map((el: any) => ({ ...el, name: el.name.toLowerCase() }))
                props.value[props.audienceId][valueField] = clearList.filter((el: any) => clearValueNames.includes(el.name)).map((el: any) => el.id)
            }
        }
        return {
            visibleItems,
            getComponentType,
            getInputWidth,
            getList,
            getInputValue,
            getShowTooltip,
            declOfNum,
            isDropdownHintOpen,
            toggleDropdownHint,
            isCollapsibleOpen,
            toggleCollapsible,
            getCollapseKey,
            copy,
            paste,
        }
    },
})
